import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CaretRight, Trash, ForkKnife, Warning } from "@phosphor-icons/react";
import { listRecipes, deleteRecipe } from "../lib/api";
import { detectAllergens } from "../lib/allergens";

export default function RecipeLibrary() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await listRecipes(); setRecipes(d.recipes || []); }
    catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete recipe?")) return;
    await deleteRecipe(id); toast.success("Deleted"); load();
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="library-page">
      <div className="pb-6 border-b border-border">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Recipe archive</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2">Library</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">Every recipe you calculate is saved locally. Click any row to open the full nutrient dashboard.</p>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-muted-foreground">Loading…</div>
      ) : recipes.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center" data-testid="empty-library">
          <ForkKnife size={40} className="mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-xl mt-4">No recipes yet</div>
          <div className="text-muted-foreground mt-1 mb-6">Calculate your first recipe to see it here.</div>
          <Link to="/" data-testid="empty-cta"
            className="inline-flex items-center h-11 px-5 bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors duration-200">
            Open Calculator
          </Link>
        </div>
      ) : (
        <div className="border border-border" data-testid="recipes-list">
          <div className="grid grid-cols-[1fr_120px_100px_100px_60px] gap-4 px-4 py-3 border-b border-border bg-muted/50 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <div>Recipe</div><div className="text-right">Calories</div><div className="text-right">Protein</div><div>Source</div><div />
          </div>
          {recipes.map((r) => {
            const allergens = detectAllergens(r.ingredients || []);
            return (
            <div key={r.id} className="grid grid-cols-[1fr_120px_100px_100px_60px] gap-4 px-4 py-4 border-b border-border last:border-b-0 hover:bg-muted/30 items-center transition-colors duration-150" data-testid={`recipe-item-${r.id}`}>
              <Link to={`/recipe/${r.id}`} className="min-w-0">
                <div className="font-medium truncate">{r.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()} · {r.ingredients?.length || 0} ingredients · {r.servings} serving{r.servings > 1 ? "s" : ""}</div>
                {(r.diet_tags || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {r.diet_tags.slice(0, 4).map(t => (
                      <span key={t} className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-border text-muted-foreground">{t.replace("_", "-")}</span>
                    ))}
                  </div>
                )}
                {allergens.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1 items-center" data-testid={`allergens-${r.id}`}>
                    <Warning size={11} weight="fill" className="text-amber-600" />
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber-700">Contains</span>
                    {allergens.map(a => (
                      <span key={a.key} title={`Detected: ${a.matched.join(", ")}`}
                        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 inline-flex items-center gap-1"
                        data-testid={`allergen-chip-${r.id}-${a.key}`}>
                        <span>{a.emoji}</span>{a.label}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
              <div className="text-right font-mono">{Math.round(r.per_serving?.calories || 0)}</div>
              <div className="text-right font-mono">{(r.per_serving?.protein || 0).toFixed(1)}g</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{r.source_type}</div>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`}
                  className="h-8 w-8 border border-border hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-colors duration-200">
                  <Trash size={13} />
                </button>
                <Link to={`/recipe/${r.id}`}
                  className="h-8 w-8 border border-border hover:bg-foreground hover:text-background flex items-center justify-center transition-colors duration-200">
                  <CaretRight size={14} />
                </Link>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}
